/* eslint-disable -- Line 3 merge conflict false positive; file has no conflict markers */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Check, Search } from 'lucide-react';
import { useTheme } from '../../../../components/common/Theme/ThemeContext';
import { ingestionService } from '../../../../components/api/ingestionService';
import { apiService } from '../../Connections/connectionsApiService';

/** Schedule object matching API payload */
export interface SchedulePayload {
  type: string;
  runAt: string;
  interval: number;
  daysOfWeek: number[];
  dayOfMonth: number;
  timeZone: string;
  cron: string;
}

export type ScheduleType = 'Manual' | 'Interval' | 'Daily' | 'Weekly' | 'Monthly' | 'Cron';

export interface ScheduleFormValues {
  id?: string;
  name: string;
  entity: string;
  destination: string;
  strategy: string;
  schedule: string;
  enabled: boolean;
  /** When strategy is Incremental: watermark column and primary key for deduplication */
  strategyOptions?: { watermarkColumn?: string; primaryKey?: string };
  /** Full schedule config for API (type, runAt, interval, daysOfWeek, dayOfMonth, timeZone, cron) */
  scheduleConfig?: SchedulePayload;
  /** Legacy / display */
  frequency?: string;
  runAt?: string;
  /** Selected destination target id (KB / Vector store / Cache / Data vault) */
  destinationId?: string;
  entityName?: string;
  destinationName?: string;
}

const DEFAULT_RUN_AT = '02:00';
const DEFAULT_TZ = 'UTC';
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function buildCron(
  type: ScheduleType,
  runAt: string,
  intervalMinutes: number,
  daysOfWeek: number[],
  dayOfMonth: number,
  customCron: string
): string {
  if (type === 'Manual') return '';
  if (type === 'Cron') return customCron.trim() || '0 0 * * *';
  const [hour = 0, min = 0] = runAt.split(':').map(Number);
  switch (type) {
    case 'Interval':
      if (intervalMinutes < 1) return '0 * * * *';
      return `*/${Math.max(1, Math.min(60, intervalMinutes))} * * * *`;
    case 'Daily':
      return `${min} ${hour} * * *`;
    case 'Weekly':
      if (daysOfWeek.length === 0) return `${min} ${hour} * * 0`;
      return `${min} ${hour} * * ${daysOfWeek.sort((a, b) => a - b).join(',')}`;
    case 'Monthly':
      return `${min} ${hour} ${Math.max(1, Math.min(31, dayOfMonth))} * *`;
    default:
      return `${min} ${hour} * * *`;
  }
}

interface AddEditScheduleModalProps {
  open: boolean;
  onClose: () => void;
  editSchedule: ScheduleFormValues | null;
  onSubmit: (data: ScheduleFormValues) => void;
}

const Toggle: React.FC<{ on: boolean; onChange: () => void; className?: string; isLight?: boolean }> = ({
  on,
  onChange,
  className = '',
  isLight = false,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onChange}
    className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors flex-shrink-0 ${on ? 'bg-indigo-600' : isLight ? 'bg-neutral-300' : 'bg-white/20'
      } ${className}`}
  >
    <span
      className={`absolute top-1 w-3.5 h-3.5 rounded-full bg-white transition-transform ${on ? 'left-[18px]' : 'left-1'
        }`}
    />
  </button>
);

/** Reusable filter input */
const ListFilter: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
  isLight?: boolean;
}> = ({ value, onChange, placeholder, className = '', isLight = false }) => (
  <div className={`relative ${className}`}>
    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isLight ? 'text-neutral-400' : 'text-zinc-600'}`} />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full pl-9 pr-9 py-2 text-sm rounded-lg outline-none transition-colors ${isLight
          ? 'bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200'
          : 'bg-zinc-800/80 text-white placeholder:text-zinc-500 focus:bg-zinc-700/80 focus:ring-1 focus:ring-white/20'
        }`}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${isLight ? 'text-neutral-400 hover:text-neutral-700' : 'text-zinc-500 hover:text-white'}`}
        aria-label="Clear filter"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

/** Section: title only, no card/border */
const Section: React.FC<{ title: string; children: React.ReactNode; className?: string; isLight?: boolean }> = ({
  title,
  children,
  className = '',
  isLight = false,
}) => (
  <div className={className}>
    <h3 className={`text-[11px] font-medium uppercase tracking-widest mb-4 ${isLight ? 'text-neutral-500' : 'text-zinc-500'}`}>{title}</h3>
    {children}
  </div>
);

const consistencyOptions = [
  { id: 'Incremental', label: 'Incremental', desc: 'Fetch only rows newer than last sync via watermark. Most efficient.' },
  { id: 'Upsert', label: 'Upsert', desc: 'Insert new, update changed by primary key. Prevents duplicates.' },
  { id: 'Full Refresh', label: 'Full Refresh', desc: 'Replace all data on every run. Guaranteed consistency.' },
  { id: 'Append Only', label: 'Append Only', desc: 'Only add new records. For immutable data and event logs.' },
];

const destinationOptions = [
  { id: 'Knowledge Base', label: '📚 Knowledge Base', desc: 'Add to an existing KB for RAG and AI agent retrieval' },
];

/** Fallback when API returns empty for Destination > Knowledge Base */
const fallbackKbList = [
  { id: 'kb-1', name: 'No knowledge bases', meta: 'Create a KB in Knowledge Base section', status: 'idle' as const },
];

type EntityCard = { id: string; label: string; meta: string; tags: string[] };

const AddEditScheduleModal: React.FC<AddEditScheduleModalProps> = ({
  open,
  onClose,
  editSchedule,
  onSubmit,
}) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const inputCls = isLight
    ? 'bg-white border border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200'
    : 'bg-zinc-800/80 text-white placeholder:text-zinc-500 focus:bg-zinc-700/80 focus:ring-1 focus:ring-white/20';
  const labelCls = isLight ? 'text-neutral-500' : 'text-zinc-500';
  const cardCls = (selected: boolean) =>
    selected
      ? isLight
        ? 'bg-indigo-50 text-neutral-900 ring-1 ring-indigo-300'
        : 'bg-indigo-500/20 text-white ring-1 ring-indigo-500/30'
      : isLight
        ? 'bg-neutral-50 border border-neutral-200 hover:bg-neutral-100 text-neutral-700'
        : 'bg-zinc-800/70 hover:bg-zinc-700/80 text-zinc-300';
  const radioCls = (selected: boolean) =>
    selected ? 'bg-indigo-500' : isLight ? 'bg-neutral-300' : 'bg-white/20';
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [selectedConsistency, setSelectedConsistency] = useState<string | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null);
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [showWmSub, setShowWmSub] = useState(false);
  const [watermarkColumn, setWatermarkColumn] = useState('updated_at');
  const [primaryKey, setPrimaryKey] = useState('id');
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('Daily');
  const [runAt, setRunAt] = useState(DEFAULT_RUN_AT);
  const [scheduleInterval, setScheduleInterval] = useState(15);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeZone, setTimeZone] = useState(DEFAULT_TZ);
  const [customCron, setCustomCron] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);

  const [dbEntities, setDbEntities] = useState<EntityCard[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);

  const [kbOptions, setKbOptions] = useState<{ id: string; name: string; meta: string; status: 'active' | 'idle' }[]>([]);
  const [kbOptionsLoading, setKbOptionsLoading] = useState(false);
  const [kbOptionsError, setKbOptionsError] = useState<string | null>(null);

  const [entityFilter, setEntityFilter] = useState('');
  const [kbFilter, setKbFilter] = useState('');
  const filtersClearedOnOpen = useRef(false);

  const filteredEntities = useMemo(() => {
    if (!entityFilter.trim()) return dbEntities;
    const q = entityFilter.toLowerCase().trim();
    return dbEntities.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.meta.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [dbEntities, entityFilter]);

  const filteredKbOptions = useMemo(() => {
    if (!kbFilter.trim()) return kbOptions;
    const q = kbFilter.toLowerCase().trim();
    return kbOptions.filter(
      (k) => k.name.toLowerCase().includes(q) || k.meta.toLowerCase().includes(q)
    );
  }, [kbOptions, kbFilter]);

  const hasDestinationTarget = selectedDestination === 'Knowledge Base' && selectedKbId != null;

  const allRequiredFilled =
    scheduleName.trim() !== '' &&
    selectedEntity != null &&
    selectedConsistency != null &&
    selectedDestination != null &&
    hasDestinationTarget;

  useEffect(() => {
    if (!open) return;
    const fetchEntities = async () => {
      setEntitiesLoading(true);
      setEntitiesError(null);
      try {
        const data = await apiService.get('/DataModels', {}, 'backend');
        const list = data?.dataModels ?? [];
        const toCard = (dm: { id: string; modelName: string; description?: string; type?: string; status?: string; tags?: string[] }): EntityCard => ({
          id: dm.id,
          label: dm.modelName || dm.id,
          meta: [dm.description, dm.type, dm.status].filter(Boolean).join(' · ') || '—',
          tags: Array.isArray(dm.tags) ? dm.tags : [],
        });
        setDbEntities(list.map(toCard));
      } catch (err) {
        console.error('Failed to load data models (entities):', err);
        setEntitiesError('Failed to load data sources. Please try again.');
        setDbEntities([]);
      } finally {
        setEntitiesLoading(false);
      }
    };
    const fetchKbOptions = async () => {
      setKbOptionsLoading(true);
      setKbOptionsError(null);
      try {
        const raw = await ingestionService.scheduler.getKnowledgeBasesForDestination();
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as any).data) ? (raw as any).data : []);
        const items = Array.isArray(list) ? list : [];
        if (items.length === 0) {
          setKbOptions(fallbackKbList);
        } else {
          setKbOptions(
            items.map((kb: Record<string, unknown>) => {
              const id = (kb.id ?? kb.Id) as string;
              const title = (kb.title ?? kb.Title) as string;
              const workspaceTitle = (kb.workspaceTitle ?? kb.WorkspaceTitle) as string | undefined;
              return {
                id: id ?? '',
                name: title || id || 'Untitled',
                meta: workspaceTitle ? `Workspace: ${workspaceTitle}` : '—',
                status: 'active' as const,
              };
            })
          );
        }
      } catch (err) {
        console.error('Failed to load knowledge bases for destination:', err);
        setKbOptionsError('Failed to load knowledge bases.');
        setKbOptions(fallbackKbList);
      } finally {
        setKbOptionsLoading(false);
      }
    };
    fetchEntities();
    fetchKbOptions();
  }, [open]);

  useEffect(() => {
    if (!open) {
      filtersClearedOnOpen.current = false;
      return;
    }
    if (!filtersClearedOnOpen.current) {
      filtersClearedOnOpen.current = true;
      setEntityFilter('');
      setKbFilter('');
    }
    if (editSchedule) {
      setScheduleName(editSchedule.name);
      setSelectedEntity(editSchedule.entity);
      setSelectedConsistency(editSchedule.strategy);
      setSelectedDestination('Knowledge Base');
      setActiveToggle(editSchedule.enabled);
      setWatermarkColumn(editSchedule.strategyOptions?.watermarkColumn ?? 'updated_at');
      setPrimaryKey(editSchedule.strategyOptions?.primaryKey ?? 'id');
      const sc = editSchedule.scheduleConfig;
      if (sc?.type) {
        setScheduleType(sc.type as ScheduleType);
        setRunAt(sc.runAt || DEFAULT_RUN_AT);
        setScheduleInterval(typeof sc.interval === 'number' ? sc.interval : 15);
        setDaysOfWeek(Array.isArray(sc.daysOfWeek) ? sc.daysOfWeek : [1]);
        setDayOfMonth(typeof sc.dayOfMonth === 'number' ? sc.dayOfMonth : 1);
        setTimeZone(sc.timeZone ?? DEFAULT_TZ);
        setCustomCron(sc.cron ?? '');
      } else {
        if (editSchedule.runAt) setRunAt(editSchedule.runAt);
        const [freq, time] = (editSchedule.schedule || '').split(' ');
        if (time && !editSchedule.runAt) setRunAt(time);
        if (editSchedule.frequency) {
          const f = editSchedule.frequency.toLowerCase();
          if (f.includes('15') || f.includes('min')) setScheduleType('Interval');
          else if (f.includes('30')) setScheduleType('Interval');
          else if (f.includes('hour')) setScheduleType('Interval');
          else if (f.includes('week')) setScheduleType('Weekly');
          else setScheduleType('Daily');
        }
      }
      setSelectedKbId(editSchedule.destination === 'Knowledge Base' ? editSchedule.destinationId ?? null : null);
      setShowWmSub(editSchedule.strategy === 'Incremental');
    } else {
      setScheduleName('');
      setSelectedEntity(null);
      setSelectedConsistency(null);
      setSelectedDestination('Knowledge Base');
      setSelectedKbId(null);
      setShowWmSub(false);
      setWatermarkColumn('updated_at');
      setPrimaryKey('id');
      setScheduleType('Daily');
      setRunAt(DEFAULT_RUN_AT);
      setScheduleInterval(15);
      setDaysOfWeek([1]);
      setDayOfMonth(1);
      setTimeZone(DEFAULT_TZ);
      setCustomCron('');
      setActiveToggle(true);
    }
  }, [open, editSchedule]);

  const cronOutput = useMemo(
    () => buildCron(scheduleType, runAt, scheduleInterval, daysOfWeek, dayOfMonth, customCron),
    [scheduleType, runAt, scheduleInterval, daysOfWeek, dayOfMonth, customCron]
  );

  const handleSubmit = () => {
    if (!allRequiredFilled) return;
    const strategy = selectedConsistency || (editSchedule?.strategy ?? 'Incremental');
    const destId = selectedKbId ?? editSchedule?.destinationId;
    const entityNameDisplay = selectedEntity ? dbEntities.find((e) => e.id === selectedEntity)?.label : editSchedule?.entityName;
    const destNameDisplay = kbOptions.find((k) => k.id === selectedKbId)?.name ?? editSchedule?.destinationName;
    const scheduleConfig: SchedulePayload = {
      type: scheduleType,
      runAt: scheduleType === 'Manual' ? '' : runAt,
      interval: scheduleType === 'Interval' ? scheduleInterval : 0,
      daysOfWeek: scheduleType === 'Weekly' ? [...daysOfWeek].sort((a, b) => a - b) : [],
      dayOfMonth: scheduleType === 'Monthly' ? dayOfMonth : 0,
      timeZone,
      cron: cronOutput,
    };
    onSubmit({
      id: editSchedule?.id,
      name: scheduleName || (editSchedule?.name ?? 'New Schedule'),
      entity: selectedEntity || (editSchedule?.entity ?? 'entity'),
      destination: 'Knowledge Base',
      strategy,
      schedule: `${scheduleType} ${runAt}`,
      enabled: activeToggle,
      strategyOptions:
        strategy === 'Incremental' ? { watermarkColumn, primaryKey } : undefined,
      scheduleConfig,
      frequency: scheduleType,
      runAt,
      destinationId: destId ?? undefined,
      entityName: entityNameDisplay,
      destinationName: destNameDisplay,
    });
    onClose();
  };

  if (!open) return null;

  const title = editSchedule ? 'Edit Schedule' : 'Add Schedule';
  const sub = editSchedule ? editSchedule.name : 'Configure a new automated data sync job';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div className={`absolute inset-0 ${isLight ? 'bg-black/40' : 'bg-black/70'}`} onClick={onClose} aria-hidden />
      <div
        className={`relative w-[85vw] max-w-[1100px] max-h-[90vh] flex flex-col rounded-2xl overflow-hidden border ${isLight ? 'bg-white border-neutral-200 shadow-xl' : 'bg-zinc-950 border-zinc-800/80'
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-8 py-5 flex-shrink-0 ${isLight ? 'bg-neutral-50 border-b border-neutral-200' : 'bg-zinc-900/50'}`}>
          <div>
            <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h2>
            <p className={`text-sm mt-0.5 ${labelCls}`}>{sub}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isLight ? 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100' : 'text-zinc-500 hover:text-white hover:bg-zinc-800'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="px-8 pb-8">
            {/* Job name + Active */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
              <div className="min-w-0 flex-1 max-w-md">
                <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>
                  Job name <span className="text-amber-400">*</span>
                </label>
                <input
                  type="text"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  placeholder="e.g. Daily Customer Sync"
                  className={`w-full px-4 py-2.5 text-sm rounded-lg outline-none transition-colors ${inputCls}`}
                />
                {scheduleName.trim() === '' && (
                  <p className="text-xs text-amber-400 mt-1.5">Name is required</p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-[11px] font-medium uppercase tracking-widest ${labelCls}`}>Active</span>
                <Toggle on={activeToggle} onChange={() => setActiveToggle(!activeToggle)} isLight={isLight} />
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 xl:gap-16">
              <div className="space-y-10">
                <Section title="Data source" isLight={isLight}>
                  <p className={`text-xs mb-3 ${labelCls}`}>Database tables &amp; queries</p>
                  {entitiesError && (
                    <p className="text-xs text-amber-500 mb-2">{entitiesError}</p>
                  )}
                  {!entitiesLoading && selectedEntity == null && (
                    <p className="text-xs text-amber-500 mb-2">Select an entity (required)</p>
                  )}
                  {entitiesLoading ? (
                    <p className={`text-xs mb-3 ${labelCls}`}>Loading entities…</p>
                  ) : (
                    <div className="space-y-3">
                      <ListFilter
                        value={entityFilter}
                        onChange={setEntityFilter}
                        placeholder="Search entities…"
                        isLight={isLight}
                      />
                      <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                        {filteredEntities.map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => setSelectedEntity(e.id)}
                            className={`rounded-lg p-3 text-left transition-colors cursor-pointer ${cardCls(selectedEntity === e.id)}`}
                          >
                            <div className="text-xs font-medium truncate text-inherit">{e.label}</div>
                            <div className={`text-[11px] truncate mt-0.5 ${labelCls}`}>{e.meta}</div>
                            {e.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {e.tags.map((t) => (
                                  <span key={t} className="text-[10px] text-zinc-500">{t}</span>
                                ))}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      {filteredEntities.length === 0 && dbEntities.length > 0 && (
                        <p className={`text-xs py-2 ${labelCls}`}>No entities match your filter.</p>
                      )}
                    </div>
                  )}
                </Section>
                <Section title="Sync strategy" isLight={isLight}>
                  <p className={`text-xs mb-3 ${labelCls}`}>Consistency &amp; deduplication</p>
                  {selectedConsistency == null && (
                    <p className="text-xs text-amber-400 mb-2">Select a strategy (required)</p>
                  )}
                  <div className="space-y-1.5">
                    {consistencyOptions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedConsistency(c.id);
                          setShowWmSub(c.id === 'Incremental');
                        }}
                        className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${cardCls(selectedConsistency === c.id)}`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center ${radioCls(selectedConsistency === c.id)}`}
                        >
                          {selectedConsistency === c.id && <Check className="w-2.5 h-2.5 text-white" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{c.label}</div>
                          <div className={`text-xs mt-0.5 ${labelCls}`}>{c.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  {showWmSub && (
                    <div className="mt-4 flex flex-wrap gap-4">
                      <div>
                        <label className={`text-[11px] block mb-1.5 ${labelCls}`}>Watermark column</label>
                        <select
                          value={watermarkColumn}
                          onChange={(e) => setWatermarkColumn(e.target.value)}
                          className={`px-3 py-2 text-xs rounded-lg outline-none min-w-[120px] ${inputCls}`}
                        >
                          <option value="updated_at">updated_at</option>
                          <option value="created_at">created_at</option>
                          <option value="last_modified">last_modified</option>
                          <option value="modified_at">modified_at</option>
                          <option value="synced_at">synced_at</option>
                        </select>
                      </div>
                      <div>
                        <label className={`text-[11px] block mb-1.5 ${labelCls}`}>Primary key</label>
                        <select
                          value={primaryKey}
                          onChange={(e) => setPrimaryKey(e.target.value)}
                          className={`px-3 py-2 text-xs rounded-lg outline-none min-w-[120px] ${inputCls}`}
                        >
                          <option value="id">id</option>
                          <option value="customer_id">customer_id</option>
                          <option value="email">email</option>
                          <option value="uuid">uuid</option>
                          <option value="pk">pk</option>
                        </select>
                      </div>
                    </div>
                  )}
                </Section>
              </div>
              <div className="space-y-10">
                <Section title="Destination" isLight={isLight}>
                  <p className={`text-xs mb-3 ${labelCls}`}>Where to sync</p>
                  {selectedDestination == null && (
                    <p className="text-xs text-amber-400 mb-2">Select a destination (required)</p>
                  )}
                  {selectedDestination != null && !hasDestinationTarget && (
                    <p className="text-xs text-amber-400 mb-2">
                      Select a knowledge base
                    </p>
                  )}
                  <div className="flex flex-col gap-2">
                    {destinationOptions.map((d) => (
                      <div key={d.id}>
                        <button
                          type="button"
                          onClick={() => {
                            const next = selectedDestination === d.id ? null : d.id;
                            setSelectedDestination(next);
                            setSelectedKbId(null);
                          }}
                          className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${cardCls(selectedDestination === d.id)}`}
                        >
                          <span
                            className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center ${radioCls(selectedDestination === d.id)}`}
                          >
                            {selectedDestination === d.id && <Check className="w-2.5 h-2.5 text-white" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{d.label}</div>
                            <div className={`text-xs mt-0.5 line-clamp-2 ${labelCls}`}>{d.desc}</div>
                          </div>
                        </button>
                        {selectedDestination === d.id && (
                          <div className="mt-3">
                            <div
                              className={`rounded-lg overflow-hidden max-h-[220px] overflow-y-auto min-h-[100px] ${isLight ? 'bg-neutral-50 border border-neutral-200' : 'bg-zinc-800/50'
                                }`}
                            >
                              {kbOptionsError && (
                                <p className="text-xs text-amber-500 px-3 py-2">{kbOptionsError}</p>
                              )}
                              {kbOptionsLoading ? (
                                <p className="text-xs text-gray-500 px-3 py-2">Loading knowledge bases…</p>
                              ) : kbOptions.length === 0 ? (
                                <p className="text-xs text-gray-500 px-3 py-2">No knowledge bases found.</p>
                              ) : (
                                <>
                                  <div className={`sticky top-0 z-10 px-3 py-2 ${isLight ? 'bg-neutral-50' : 'bg-zinc-900/95'}`}>
                                    <ListFilter
                                      value={kbFilter}
                                      onChange={setKbFilter}
                                      placeholder="Search knowledge bases…"
                                      isLight={isLight}
                                    />
                                  </div>
                                  <div className="max-h-[180px] overflow-y-auto">
                                    {filteredKbOptions.map((kb) => (
                                      <button
                                        key={kb.id}
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setSelectedKbId(selectedKbId === kb.id ? null : kb.id);
                                        }}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-xs transition-colors ${selectedKbId === kb.id
                                            ? isLight ? 'bg-indigo-50 text-neutral-900' : 'bg-indigo-500/20 text-white'
                                            : isLight ? 'hover:bg-neutral-100 text-neutral-600' : 'hover:bg-zinc-700/80 text-zinc-300'
                                          }`}
                                      >
                                        <span
                                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${kb.status === 'active' ? 'bg-emerald-500' : 'bg-zinc-500'
                                            }`}
                                        />
                                        <span className="flex-1">{kb.name}</span>
                                        <span className="text-zinc-500">{kb.meta}</span>
                                        {selectedKbId === kb.id && <Check className="w-3.5 h-3.5 text-indigo-400" />}
                                      </button>
                                    ))}
                                  </div>
                                  {filteredKbOptions.length === 0 && kbOptions.length > 0 && (
                                    <p className="text-xs text-zinc-500 px-3 py-2">No knowledge bases match your filter.</p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
                <Section title="Schedule" isLight={isLight}>
                  <p className={`text-xs mb-3 ${labelCls}`}>When to run this sync</p>
                  <div className="space-y-4">
                    <div>
                      <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Frequency</label>
                      <select
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
                        className={`w-full max-w-xs px-4 py-2.5 text-sm rounded-lg outline-none transition-colors ${inputCls}`}
                      >
                        <option value="Manual">Manual only</option>
                        <option value="Interval">Every X minutes</option>
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Cron">Custom cron</option>
                      </select>
                    </div>
                    {(scheduleType === 'Daily' || scheduleType === 'Weekly' || scheduleType === 'Monthly') && (
                      <div>
                        <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Run at (UTC)</label>
                        <input
                          type="time"
                          value={runAt}
                          onChange={(e) => setRunAt(e.target.value)}
                          className={`px-4 py-2.5 text-sm rounded-lg outline-none transition-colors ${inputCls}`}
                        />
                      </div>
                    )}
                    {scheduleType === 'Interval' && (
                      <div>
                        <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Interval (minutes)</label>
                        <input
                          type="number"
                          min={1}
                          max={60}
                          value={scheduleInterval}
                          onChange={(e) =>
                            setScheduleInterval(Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1)))
                          }
                          className={`px-4 py-2.5 text-sm rounded-lg outline-none w-24 ${inputCls}`}
                        />
                      </div>
                    )}
                    {scheduleType !== 'Manual' && scheduleType !== 'Cron' && (
                      <div>
                        <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Time zone</label>
                        <input
                          type="text"
                          value={timeZone}
                          onChange={(e) => setTimeZone(e.target.value)}
                          placeholder="UTC"
                          className={`px-4 py-2.5 text-sm rounded-lg outline-none w-28 ${inputCls}`}
                        />
                      </div>
                    )}
                    {scheduleType === 'Weekly' && (
                      <div>
                        <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Days of week</label>
                        <div className="flex flex-wrap gap-2">
                          {DAYS_OF_WEEK.map((d) => (
                            <button
                              key={d.value}
                              type="button"
                              onClick={() =>
                                setDaysOfWeek((prev) =>
                                  prev.includes(d.value)
                                    ? prev.filter((x) => x !== d.value)
                                    : [...prev, d.value].sort((a, b) => a - b)
                                )
                              }
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                daysOfWeek.includes(d.value)
                                  ? isLight
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-indigo-500/80 text-white'
                                  : isLight
                                    ? 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                    : 'bg-zinc-800 text-zinc-400 hover:text-white'
                              }`}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {scheduleType === 'Monthly' && (
                      <div className="max-w-[120px]">
                        <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Day of month (1–31)</label>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={dayOfMonth}
                          onChange={(e) =>
                            setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1)))
                          }
                          className={`w-full px-4 py-2.5 text-sm rounded-lg outline-none ${inputCls}`}
                        />
                      </div>
                    )}
                    {scheduleType === 'Cron' && (
                      <div>
                        <label className={`text-[11px] font-medium uppercase tracking-widest block mb-2 ${labelCls}`}>Cron expression</label>
                        <input
                          type="text"
                          value={customCron}
                          onChange={(e) => setCustomCron(e.target.value)}
                          placeholder="e.g. 0 2 * * *"
                          className={`w-full max-w-md px-4 py-2.5 text-sm rounded-lg outline-none ${inputCls}`}
                        />
                        <p className={`text-xs mt-1 ${labelCls}`}>5-field: minute hour day-of-month month day-of-week</p>
                      </div>
                    )}
                    {scheduleType !== 'Manual' && cronOutput && (
                      <p className={`text-xs ${labelCls}`}>Cron: {cronOutput}</p>
                    )}
                  </div>
                </Section>
              </div>
            </div>
          </div>
        </div>
        <div className={`flex gap-3 px-8 py-5 flex-shrink-0 border-t ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-zinc-900/50 border-zinc-800/80'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`flex-1 py-2.5 px-5 text-sm font-medium rounded-lg transition-colors inline-flex items-center justify-center ${isLight ? 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allRequiredFilled}
            className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="w-4 h-4" />
            Save Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditScheduleModal;
